import { FirmAssetBalanceStatistics, FirmRecord, FirmStatistics } from '@tassat/fred-definitions';
import { FirmStatsMapped } from '@tassat/fred-extended-definitions';
import { createSelector } from 'reselect';

import { AdminState } from 'Reducers/store/adminStore';
import { ClientState } from 'Reducers/store/clientStore';

import { getWalletsForFirm } from './accounts';
import { getFirmAssetStats, getFirmStats, getFirms } from './common';
import { getActiveApiTypes } from './firmApiTypes';
import { getClientUsers } from './users';

const getFirmId = (_state: AdminState | ClientState, firmId: string) => firmId;
const getUserId = (_state: AdminState | ClientState, userId: string, _includeClosed: boolean) => userId;
const getIncludeClosed = (_state: AdminState | ClientState, _userId: string, includeClosed: boolean) => includeClosed;

export const getClientFirm = createSelector(getFirms, (firms): FirmRecord => {
    const keys = Object.keys(firms);
    if (keys.length !== 1) {
        return {} as FirmRecord;
    } else {
        return firms[keys[0]];
    }
});

export const getFirstFirmName = createSelector(getFirms, (firms): string | false => {
    const keys = Object.keys(firms);

    return keys.length > 0 && firms[keys[0]].Name;
});

// export const getFirmByUserId = createSelector([getUsers, getFirms, getUserId], (users, firms, userId): FirmRecord => {
//   const userObj = users[userId];
//   if (!userObj) {
//     return {} as FirmRecord;
//   }

//   const userFirmId = Object.keys(firms).find(firmId => firmId === userObj.FirmId);
//   if (!userFirmId) {
//     return {} as FirmRecord;
//   }

//   return firms[userFirmId];
// });

export const getFirmByFirmId = createSelector([ getFirms, getFirmId ], (firms, firmId): FirmRecord => {
    return firms[firmId] || undefined;
});

// export const firmHasAPIUsers = createSelector([getUsers, getFirmId], (users, firmId) => {
//   return Object.keys(users)
//     .filter(uId => users[uId]
//       && !users[uId].IsDeleted
//       && users[uId].IsTokenAPIUser
//       && users[uId].FirmId === firmId)
//     .map(uId => users[uId]).length > 0;
// });

export const getFirmsArray = createSelector(
    [ getFirms, getIncludeClosed ],
    (firms, includeClosed): FirmRecord[] => {
        return Object.keys(firms)
            .map(key => firms[key])
            .filter(firms => {
                if (includeClosed) {
                    return firms && !firms.IsDeleted;
                }

                return firms && !firms.IsClosed && !firms.IsDeleted;
            });
    }
);

export const getFirmsWithStatsArray = createSelector(
    [ getFirms, getFirmStats, getIncludeClosed, getFirmAssetStats ],
    (firms, firmStats, includeClosed, firmAssetStats): FirmStatsMapped[] => {
        const firmsArray = Object.keys(firms)
            .map(id => firms[id])
            .filter(firm => {
                if (firm.IsDeleted) {
                    return false;
                }

                return includeClosed ? true : !firm.IsClosed;
            });

        return firmsArray.map(firm => {
            if (firmStats[firm.FirmId] === undefined){
                return {
                    firm: firm,
                    firmStats: {} as FirmStatistics,
                    firmAssetStats: {} as FirmAssetBalanceStatistics
                };
            } else {
                return {
                    firm: firm,
                    firmStats: firmStats[firm.FirmId],
                    firmAssetStats: firmAssetStats[firm.FirmId]
                };
            }
        });
    }
);

export const getAPIFirmsArray = createSelector(
    [ getFirms, getIncludeClosed, getActiveApiTypes ],
    (firms, includeClosed, activeApiTypes): FirmRecord[] => {
        const firmsArray = Object.keys(firms)
            .map(key => firms[key])
            .filter(firm => (firm.IsTokenAPIFirm || activeApiTypes[firm.FirmId]) &&
                !firm.IsDeleted &&
                (includeClosed || (!includeClosed && !firm.IsClosed)));

        return firmsArray;
    }
);

export const getLFFirmsArray = createSelector(
    [getFirms],
    (firms): Array<FirmRecord> => {
        const firmsArray = Object.keys(firms)
            .map(key => firms[key])
            .filter(firm => firm.IsLiquidityFacility &&
                !firm.IsDeleted &&
                !firm.IsClosed);

        return firmsArray;
    }
);

export const getFirmFromFirms = createSelector([ getFirms, getFirmId ], (firms, firmId) => {
    const firm = firms[firmId];

    if (!firm) {
        return;
    }

    return firm;
});

// export const getFirmsByUserAcgs = createSelector(
//     [ getAccountStats, getOpenAccountsByUserAcgs, getAllAccountsByUserAcgs, getFirms, getIncludeClosed ],
//     (accountStats, acgAccounts, accountsByUserAcg, firms, includeClosed): FirmStatsMapped[] => {
//         const firmIds = {};
//         const firmsArray = [];
//         accountsByUserAcg.forEach(acct => {
//             const firm = firms[acct.FirmId];
//             if (firm && !firm.IsDeleted && (includeClosed || (!includeClosed && !firm.IsClosed)) && !firmIds[firm.FirmId]) {
//                 firmIds[firm.FirmId] = true;
//                 const firmStats = {
//                     FirmId: firm.FirmId,
//                     BegAvailBal: 0,
//                     BlockchainBal: 0,
//                     EndAvailBal: 0,
//                     PendDeps: 0,
//                     PendDepCnt: 0,
//                     PendRdms: 0,
//                     PendRdmCnt: 0,
//                     PendSnds: 0,
//                     PendSndCnt: 0,
//                     TodaysDeps: 0,
//                     TodaysDepCnt: 0,
//                     TodaysRdms: 0,
//                     TodaysRdmCnt: 0,
//                     TodaysSnds: 0,
//                     TodaysSndCnt: 0
//                 };

//                 acgAccounts.map((account) => accountStats[account.AccountId])
//                     .filter((accountStat) => accountStat?.FirmId === firm.FirmId)
//                     .forEach((accountStat: AccountStatisticsReport) => {
//                         firmStats.BegAvailBal += parseFloat(accountStat.BegAvailBal);
//                         firmStats.BlockchainBal += parseFloat(accountStat.BlockchainBal);
//                         firmStats.EndAvailBal += parseFloat(accountStat.EndAvailBal);
//                         firmStats.PendDeps += parseFloat(accountStat.PendDeps);
//                         firmStats.PendDepCnt += accountStat.PendDepCnt;
//                         firmStats.PendRdms += parseFloat(accountStat.PendRdms);
//                         firmStats.PendRdmCnt += accountStat.PendRdmCnt;
//                         firmStats.PendSnds += parseFloat(accountStat.PendSnds);
//                         firmStats.PendSndCnt += accountStat.PendSndCnt;
//                         firmStats.TodaysDeps += parseFloat(accountStat.TodaysDeps);
//                         firmStats.TodaysDepCnt += accountStat.TodaysDepCnt;
//                         firmStats.TodaysRdms += parseFloat(accountStat.TodaysRdms);
//                         firmStats.TodaysRdmCnt += accountStat.TodaysRdmCnt;
//                         firmStats.TodaysSnds += parseFloat(accountStat.TodaysSnds);
//                         firmStats.TodaysSndCnt += accountStat.TodaysSndCnt;
//                     });

//                 firmsArray.push({
//                     firm,
//                     firmStats
//                 });
//             }
//         });

//         return firmsArray;
//     }
// );

export const getFirmStatsObj = createSelector(
    [ getClientUsers, getWalletsForFirm, getFirmId ],
    (clientUsers, wallets, firmId) => {
        let activeUsers = 0;
        let lockedActiveUsers = 0;
        let apiUsers = 0;
        let lockedApiUsers = 0;

        clientUsers.forEach(user => {
            if (user.FirmId === firmId) {
                activeUsers++;
                if (user.IsTokenAPIUser && !user.IsLocked) {
                    apiUsers++;
                } else if (user.IsTokenAPIUser && user.IsLocked) {
                    apiUsers++;
                    lockedActiveUsers++;
                    lockedApiUsers++;
                } else if (!user.IsTokenAPIUser && user.IsLocked) {
                    lockedActiveUsers++;
                }
            }
        });

        const lockedWallets = wallets.filter(wallet => wallet.IsLocked);

        return {
            wallets: wallets.length,
            lockedWallets: lockedWallets.length,
            activeUsers: activeUsers,
            lockedActiveUsers: lockedActiveUsers,
            apiUsers: apiUsers,
            lockedApiUsers: lockedApiUsers
        };
    }
);